import { NestedTabs } from "../../../../components-common/tabs/NestedTabs";
import { FC, useEffect, useState } from "react";
import { WindowDataForm } from "./WindowForm";
import { FormikContextType, useFormikContext } from "formik";
import { TypeCreationParametersTable } from "../TypeCreationParametersTable";
import { application } from "../../../../_runtime/bootstrap";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { Loader } from "taltech-styleguide";
import { useNavigate, useParams } from "react-router-dom";
import { FormProps } from "@components-common/forms";
import { v4 as uuidv4 } from "uuid";
import { useNotifications } from "@hooks";
import {
  handleTypeParameters,
  handleTypeParametersRequests,
} from "../component-type-creation/_helpers";
import { useUserContext } from "../../../../_runtime/iam/session.context";
import { roleMapper } from "../../../../_runtime/iam/session.provider";

/**
 * Array: tabs
 *
 * This array contains configuration objects for different tabs, each with an ID, title, and components to be rendered within the tab.
 *
 * @property {string} id - The ID of the tab.
 * @property {string} title - The title of the tab.
 * @property {Array} components - An array of components to be rendered within the tab.
 */
const tabs: any = [
  {
    id: "data",
    title: "Data",
    components: [
      {
        Component: WindowDataForm,
      },
    ],
  },
  {
    id: "type_parameters",
    title: "Type Parameters",
    components: [
      {
        Component: TypeCreationParametersTable,
      },
    ],
  },
];

/**
 * Function: mapParametersToCreateType
 *
 * This asynchronous function fetches parameters for a specific category (WINDOWS) and maps them to a new structure.
 * It updates the formik context with the new parameters and sets the properties of the type parameters tab.
 *
 * @param {FormikContextType<any>} formik - The formik context for managing form state.
 * @param {Function} setLoading - Function to set the loading state.
 * @param {Function} setOriginalParameters - Function to set the original parameters state.
 *
 * Returns:
 * @returns {Promise<void>} A promise that resolves once the parameters have been fetched and mapped.
 */
const mapParametersToCreateType = async (
  formik: FormikContextType<any>,
  setLoading: any,
  setOverridenParameters: any,
  isEditForm: boolean
) => {
  const windowParameters = await application
    .getHttpClient()
    .get("/buildingms/categories/parameters?category=WINDOWS")
    .then((response: any) => {
      return response.body!.items;
    });

  let existentTypeParameters: any[] = [];
  if (isEditForm) {
    existentTypeParameters = await application
      .getHttpClient()
      .get(`/buildingms/types/parameters?elementId=${formik.values.id}`)
      .then((response: any) => {
        setOverridenParameters(response.body!.items);
        return response.body!.items;
      });
  }

  const newWindowParameters = windowParameters.map((param: any) => {
    const typeParam = existentTypeParameters.find(
      (typeParam: any) => typeParam.parameterId === param.id
    );
    let newParam: any = {
      id: param.id,
      name: param.name,
      description: param.description,
      defaultValue: param.value,
      category: param.category,
      type: param.type,
    };

    if (typeParam) {
      newParam = {
        ...newParam,
        value: typeParam.value,
      };
    }

    return newParam;
  });

  formik.setFieldValue("typeParameters", newWindowParameters);
  tabs[1].components[0].props = {
    tableItems: newWindowParameters,
    title: "Window Parameters",
  };
  setLoading(false);
};

/**
 * Functional Component: TypeWindowScreenTabs
 *
 * This component manages the tabs for the window type screen. It initializes the form state, fetches parameters,
 * and handles form submission for creating or editing window types.
 *
 * Props:
 * @param {FormProps<any>} props - The properties passed to the form component.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the tabs for the window type screen.
 */
export const TypeWindowScreenTabs: FC<FormProps<any>> = (props) => {
  const nav = useNavigate();
  const useUserNotifications = useNotifications();
  const [overridenParameters, setOverridenParameters] = useState<any>([]);
  const formik: any = useFormikContext();
  const [loading, setLoading] = useLoading(true);
  const { windowTypeId } = useParams();
  const { userId, userRole } = useUserContext();

  useEffect(() => {
    mapParametersToCreateType(
      formik,
      setLoading,
      setOverridenParameters,
      props.id !== "form_windows_types_create"
    );
  }, []);

  useEffect(() => {
    if (formik.isSubmitting) {
      if (formik.isValid) {
        if (formik.values.style === "ISOLATED") {
          const isWindowHeightValid = validateWindowHeight(formik.values);
          if (!isWindowHeightValid) {
            useUserNotifications.error({
              title:
                "Window height and sill height together should not exceed 100%.",
            });
            return;
          }
        }
        if (props.id === "form_windows_types_create") {
          const data = {
            ...formik.values,
            sourceId: userId,
            sourceType: userRole === roleMapper.Student ? "USER" : "SYSTEM",
            id: uuidv4(),
          };
          const typeParameters = handleTypeParameters(
            data.id,
            data,
            overridenParameters
          );

          delete data["typeParameters"];
          application
            .getHttpClient()
            .post("/buildingsms/types/elements/windows", data)
            .then(() => {
              handleTypeParametersRequests(typeParameters);
              nav(-1);
            })
            .catch((error: any) => formik.setSubmitting(false));
        } else {
          const typeParameters = handleTypeParameters(
            windowTypeId as string,
            formik.values,
            overridenParameters
          );
          delete formik.values["typeParameters"];
          application
            .getHttpClient()
            .put(
              `/buildingsms/types/elements/windows/${windowTypeId}`,
              formik.values
            )
            .then(() => {
              handleTypeParametersRequests(typeParameters);
              nav(-1);
            })
            .catch((error: any) => formik.setSubmitting(false));
        }
      }
      formik.setSubmitting(false);
    }
  }, [formik.isSubmitting]);

  return loading ? (
    <Loader overlay />
  ) : (
    <NestedTabs tabs={tabs} parentPath="" />
  );
};

/**
 * Function: validateWindowHeight
 *
 * Validates the window height and sill height based on specified rules.
 *
 * @param {object} values - The values containing window height and sill height details.
 *
 * @returns {boolean} - Returns true if the window height and sill height are valid, otherwise false.
 */
const validateWindowHeight = (values: any) => {
  const { height, sillHeight } = values;

  if (height.maximum) return true;

  if (height.position === "RELATIVE" && sillHeight.position === "RELATIVE") {
    if (height.length + sillHeight.length > 100) {
      return false;
    }
  }

  return true;
};
